import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';

export const ThemeModes = Object.freeze({
  Light: 'light',
  Dark: 'dark',
  System: 'system'
});

const SELECTABLE_MODES = Object.values(ThemeModes);

const DARK_COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * @param {unknown} mode
 * @returns {boolean}
 */
export const isThemeMode = mode => SELECTABLE_MODES.includes(mode);

/**
 * Reads the user's stored choice. Anything missing or unknown resolves to light.
 * @returns {'light'|'dark'|'system'}
 */
export const readStoredThemeMode = () => {
  try {
    const stored = window.localStorage.getItem(PERSISTENCE_KEYS.THEME_MODE);
    return isThemeMode(stored) ? stored : ThemeModes.Light;
  } catch {
    return ThemeModes.Light;
  }
};

/**
 * @param {'light'|'dark'|'system'} mode
 * @returns {boolean} whether the choice was stored
 */
export const storeThemeMode = mode => {
  try {
    window.localStorage.setItem(PERSISTENCE_KEYS.THEME_MODE, mode);
    return true;
  } catch {
    return false;
  }
};

/**
 * @returns {MediaQueryList|null}
 */
export const getDarkSchemeMediaQuery = () =>
  typeof window.matchMedia === 'function' ? window.matchMedia(DARK_COLOR_SCHEME_QUERY) : null;

/**
 * Resolves the mode that is actually painted. The pre-render script in `public/index.html` repeats
 * this rule, the `darkMode` flag name and the `theme-mode` storage key; keep them in sync.
 * @param {Object} params
 * @param {'light'|'dark'|'system'} params.mode user's choice
 * @param {boolean} params.prefersDark OS preference
 * @param {boolean} params.isEnabled dark mode feature flag
 * @returns {'light'|'dark'}
 */
export const resolveThemeMode = ({ mode, prefersDark, isEnabled }) => {
  if (!isEnabled) return ThemeModes.Light;
  if (mode === ThemeModes.System) return prefersDark ? ThemeModes.Dark : ThemeModes.Light;
  return mode === ThemeModes.Dark ? ThemeModes.Dark : ThemeModes.Light;
};
